<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ACCOUNT_HISTORY_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>

      <template #cell(amount)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : '' }}
        </span>
      </template>
      <template #cell(dateCreated)="data">
        {{ toDateWithCustomTime(data.value) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Open' ? 'CLOSE_SUSPICIOUS_TRANSACTION_CASE' : 'REOPEN_SUSPICIOUS_TRANSACTION_CASE')"
              @click="openCloseCaseStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Open' ? $t('Close Case') : $t('Open Case') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="fetchSuspiciousTransactionReport"
      @cancel="resetFilters"
    >

      <b-form>
        <b-row class="match-height">
          <!-- Account Number -->
          <b-col md="6">
            <InputField
              id="account-number"
              v-model="filter.accountNumber"
              :label="$t('Account Number')"
              :placeholder="$t('Enter Account Number')"
              type="text"
            />
          </b-col>
          <!-- Account Number -->
          <b-col md="6">
            <InputField
              id="counterparty-account-number"
              v-model="filter.counterpartyAccountNumber"
              :label="$t('Counterparty Account Number')"
              :placeholder="$t('Enter Counterparty Account Number')"
              type="text"
            />
          </b-col>
        </b-row>

        <b-row class="match-height">
          <!-- Reference Number -->
          <b-col md="6">
            <InputField
              id="reference-number"
              v-model="filter.referenceNumber"
              :label="$t('Reference Number')"
              :placeholder="$t('Enter Reference Number')"
              type="text"
              for="reference-number"
            />
          </b-col>
          <!-- Reference Number -->
          <!-- Instrument Number -->
          <b-col md="6">
            <InputField
              id="instrument-number"
              v-model="filter.instrumentNumber"
              :label="$t('Instrument Number')"
              :placeholder="$t('Enter Instrument Number')"
              type="text"
              for="instrument-number"
            />
          </b-col>
          <!-- Instrument Number -->
        </b-row>

        <b-row class="match-height">
          <!-- Start Date -->
          <b-col md="6">
            <DatePickerField
              id="start-date"
              v-model="filter.startDate"
              :min-date="new Date()"
              :label="$t('Start Date')"
              :config="datePickerConfig"
              :placeholder="$t('Start Date')"
            />
          </b-col>
          <!-- Start Date -->
          <!-- End Date -->
          <b-col md="6">
            <DatePickerField
              id="end-date"
              v-model="filter.endDate"
              :min-date="new Date()"
              :label="$t('End Date')"
              :config="datePickerConfig"
              :placeholder="$t('End Date')"
            />
          </b-col>
          <b-col md="6">
            <SelectField
              id="action"
              v-model="filter.action"
              :label="$t('Action')"
              :placeholder="$t('Select')"
              :options="translatedTransactionRuleAction"
              :reduce="option => option.value"
              item="label"
              :error-name="$t('Action')"
              rules="required"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="6">
            <SelectField
              id="transaction-direction"
              v-model="filter.transactionDirection"
              :label="$t('Transaction Direction')"
              :placeholder="$t('Enter Transaction Direction')"
              :options="translatedTransactionDirectionOptions"
              :reduce="option => option.value"
              item="label"
            />
          </b-col>
          <!-- End Date -->
          <b-col md="6">
            <SelectField
              id="status"
              v-model="filter.status"
              :reduce="option => option.value"
              :label="$t('Status')"
              :placeholder="$t('Select')"
              :options="translatedStatusOptions"
              item="label"
            />
          </b-col>
        </b-row>
      </b-form>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import XLSX from 'xlsx'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import Button from '../../components/button/Button.vue'
import SelectField from '../../forms/SelectField.vue'
import DatePickerField from '../../forms/DatePickerField.vue'
import toNumber from '../../../helpers/toNumber'
import {
  toCustomDateTime, toCustomDate, toDateWithYearFirst, toDateWithCustomTime,
} from '../../../helpers/formatDate'
import getStatusColor from '../../../helpers/getStatusColor'
import { fraudMonitorPermissions } from '@/helpers/permissions'

const transactionRuleAction = [
  { value: 'DoNothing', label: 'Do Nothing' },
  { value: 'PND', label: 'PND' },
  { value: 'PNC', label: 'PNC' },
  { value: 'Freeze', label: 'Freeze' },
  { value: 'Lien', label: 'Lien' },
]

const statusOptions = [
  { label: 'Open', value: 'Open' },
  { label: 'Closed', value: 'Closed' },
]

const transactionDirectionOptions = [
  { label: 'Inflow', value: 'Inflow' },
  { label: 'Outflow', value: 'Outflow' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [sweetAlert, modalMixins, tableMixins, permissionMixin],
  data() {
    return {
      toDateWithCustomTime,
      toBalance,
      getStatusColor,
      toFormatAmount,
      formatAmount,
      formatToDateWithYears: toDateWithYearFirst,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isDisabled: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Suspicious Transaction Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'counterpartyAccountNumber', sortable: true, label: 'Counterparty Account Number' },
        { key: 'counterpartyAccountName', sortable: true, label: 'Counterparty Account Name' },
        { key: 'branchCode', sortable: true, label: 'Branch Code' },
        { key: 'counterpartyBank', sortable: true, label: 'Counterparty Bank' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'ruleName', sortable: true, label: 'Rule Name' },
        { key: 'ruleDescription', sortable: true, label: 'Rule Description' },
        { key: 'currency', sortable: true, label: 'Currency' },
        { key: 'action', sortable: true, label: 'Action' },
        { key: 'transactionDirection', sortable: true, label: 'Transaction Direction' },
        { key: 'notificationEmail', sortable: true, label: 'Notification Email' },
        { key: 'customerEmail', sortable: true, label: 'Customer Email' },
        { key: 'customerPhoneNumber', sortable: true, label: 'Customer Phone Number' },
        { key: 'dateCreated', sortable: true, label: 'Transaction Date' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      filter: {
        accountNumber: '',
        counterpartyAccountNumber: '',
        startDate: null,
        endDate: null,
        instrumentNumber: '',
        status: '',
        referenceNumber: '',
        action: '',
        transactionDirection: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      selected: '',

    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map((item, index) => ({
        'S/N': index + 1,
        'Account Number': item.accountNumber,
        'Account Name': item.accountName,
        'Counterparty Account Number': item.counterpartyAccountNumber,
        'Counterparty Account Name': item.counterpartyAccountName,
        'Branch Code': item.branchCode,
        'Counterparty Bank': item.counterpartyBank,
        'Counterparty Bank Name': item.counterpartyBankName,
        'Instrument Number': item.instrumentNumber,
        Amount: item.amount === '' ? '' : formatAmount(item.amount),
        'Rule Name': item.ruleName,
        'Rule Description': item.ruleDescription,
        Currency: item.currency,
        Action: item.action,
        'Transaction Direction': item.transactionDirection,
        'Notification Email': item.notificationEmail,
        'Customer Email': item.customerEmail,
        'Customer Phone Number': item.customerPhoneNumber,
        Status: item.status,

      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTransactionRuleAction() {
      return transactionRuleAction.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedTransactionDirectionOptions() {
      return transactionDirectionOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchSuspiciousTransactionReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchSuspiciousTransactionReport()
    },
  },
  mounted() {
    this.fetchSuspiciousTransactionReport()
  },
  methods: {
    ...mapActions({
      getSuspiciousTransactions: 'reports/getSuspiciousTransactionReports',
      updateOpenCloseStatus: 'fraudmonitor/updateOpenCloseStatusAction',
    }),
    async openCloseCaseStatus(items) {
      const { id } = items
      const actionType = items.status === 'Open' ? 'Closed' : 'Open'
      const action = items.status === 'Open' ? this.$t('Close') : this.$t('Open')
      const permission = items.status === 'Open' ? fraudMonitorPermissions.CLOSE_SUSPICIOUS_TRANSACTION_CASE : fraudMonitorPermissions.REOPEN_SUSPICIOUS_TRANSACTION_CASE
      const performOpenCloseStatus = async () => {
        const requestText = this.$t('Your request to')
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            status: actionType,
          }
          const response = await this.updateOpenCloseStatus({
            statusValues, actionType, permission,
          })
          if (response.status === 200) {
            this.fetchSuspiciousTransactionReport()
            const successText = this.$t('Case was successful')
            this.alertSuccess({ text: `${requestText} ${action} ${successText}` })
            this.showSpinner = false
          }
        } catch (error) {
          const errorText = this.$t('Case failed. Reason:')
          this.alertError({ text: `${requestText} ${action} ${errorText} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const thisText = this.$t('this Case?')
      this.confirm(performOpenCloseStatus, { title: '', text: `${areYouSure} ${action} ${thisText}` })
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchSuspiciousTransactionReport()
    },
    async fetchSuspiciousTransactionReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getSuspiciousTransactions({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: fraudMonitorPermissions.VIEW_SUSPICIOUS_TRANSACTION_REPORT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alert({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getSuspiciousTransactions({
          search: this.filter,
          page: 1,
          limit: 100000,
          permission: fraudMonitorPermissions.VIEW_SUSPICIOUS_TRANSACTION_REPORT,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Suspicious Transaction Report'],
            [`${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Suspicious Transaction Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getSuspiciousTransactions({
          search: this.filter,
          page: 1,
          limit: 100000,
          permission: fraudMonitorPermissions.VIEW_SUSPICIOUS_TRANSACTION_REPORT,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Suspicious_Transaction_Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Suspicious Transaction Report'],
            [`${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = this.allSearchedData.map((item, index) => ({
            'S/N': index + 1,
            'Account Number': item.accountNumber,
            'Account Name': item.accountName,
            'Counterparty Account Number': item.counterpartyAccountNumber,
            'Counterparty Account Name': item.counterpartyAccountName,
            'Branch Code': item.branchCode,
            'Counterparty Bank': item.counterpartyBank,
            'Counterparty Bank Name': item.counterpartyBankName,
            'Instrument Number': item.instrumentNumber,
            Amount: item.amount === '' ? '' : toFormatAmount(item.amount),
            'Rule Name': item.ruleName,
            'Rule Description': item.ruleDescription,
            Currency: item.currency,
            Action: item.action,
            'Transaction Direction': item.transactionDirection,
            'Notification Email': item.notificationEmail,
            'Customer Email': item.customerEmail,
            'Customer Phone Number': item.customerPhoneNumber,
            Status: item.status,
          }))
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Suspicious Transaction Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
